import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  TablePagination,
  Grid,
  Typography,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search,
  FilterList,
  FileDownload,
  LocationOn,
} from '@mui/icons-material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { apiService, Location, UaeEmirate, UaeCity } from '../../services/apiService';
import { useNavigate } from 'react-router-dom';
import SettingsSidebar from '../../components/Common/SettingsSidebar';

const Locations: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    emirate_id: '',
    city_id: '',
    area: '',
    landmark_1: '',
    landmark_2: '',
    remarks: '',
    status: 'active',
    notes: '',
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // UAE data state
  const [emirates, setEmirates] = useState<UaeEmirate[]>([]);
  const [cities, setCities] = useState<UaeCity[]>([]);
  const [loadingUae, setLoadingUae] = useState(false);

  // Fetch data from API
  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    fetchLocations();
    fetchUaeData();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await apiService.getLocations();
      const locationsData = Array.isArray(response.data) ? response.data : [];
      setLocations(locationsData);
    } catch (error: any) {
      console.error('Error fetching locations:', error);
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      } else {
        toast.error(error.message || 'Failed to fetch locations');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUaeData = async () => {
    try {
      setLoadingUae(true);
      const [emiratesResponse, citiesResponse] = await Promise.all([
        apiService.getUaeEmirates(),
        apiService.getUaeCities()
      ]);
      
      setEmirates(Array.isArray(emiratesResponse.data) ? emiratesResponse.data : []);
      setCities(Array.isArray(citiesResponse.data) ? citiesResponse.data : []);
    } catch (error: any) {
      console.error('Error fetching UAE data:', error);
      toast.error('Failed to fetch UAE data');
    } finally {
      setLoadingUae(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDrawer = (location?: Location) => {
    if (location) {
      setIsEditing(true);
      setEditingLocation(location);
      setFormData({
        name: location.name,
        address: location.address || '',
        city: location.city || '',
        emirate_id: location.emirate_id?.toString() || '',
        city_id: location.city_id?.toString() || '',
        area: location.area || '',
        landmark_1: location.landmark_1 || '',
        landmark_2: location.landmark_2 || '',
        remarks: location.remarks || '',
        status: location.status,
        notes: location.notes || '',
      });
    } else {
      setIsEditing(false);
      setEditingLocation(null);
      setFormData({
        name: '',
        address: '',
        city: '',
        emirate_id: '',
        city_id: '',
        area: '',
        landmark_1: '',
        landmark_2: '',
        remarks: '',
        status: 'active',
        notes: '',
      });
    }
    setErrors({});
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setIsEditing(false);
    setEditingLocation(null);
    setFormData({
      name: '',
      address: '',
      city: '',
      emirate_id: '',
      city_id: '',
      area: '',
      landmark_1: '',
      landmark_2: '',
      remarks: '',
      status: 'active',
      notes: '',
    });
    setErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleEmirateChange = (e: any) => {
    const emirateId = e.target.value as string;
    setFormData(prev => ({ 
      ...prev, 
      emirate_id: emirateId,
      city_id: '' // Reset city when emirate changes
    }));
  };

  const getFilteredCities = () => {
    if (!formData.emirate_id) return [];
    return cities.filter(city => city.emirate_id.toString() === formData.emirate_id);
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.area.trim()) {
      newErrors.area = 'Area is required';
    }
    if (!formData.emirate_id) {
      newErrors.emirate_id = 'Emirate is required';
    }
    if (!formData.city_id) {
      newErrors.city_id = 'City is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setSubmitting(true);
    try {
      const locationData = {
        name: formData.area || formData.name, // Use area as location name
        address: formData.address,
        city: formData.city,
        emirate_id: formData.emirate_id ? parseInt(formData.emirate_id) : undefined,
        city_id: formData.city_id ? parseInt(formData.city_id) : undefined,
        area: formData.area,
        landmark_1: formData.landmark_1,
        landmark_2: formData.landmark_2,
        remarks: formData.remarks,
        status: formData.status,
        notes: formData.notes,
      };

      if (isEditing && editingLocation) {
        await apiService.updateLocation(editingLocation.id, locationData);
        toast.success('Location updated successfully');
      } else {
        await apiService.createLocation(locationData);
        toast.success('Location created successfully');
      }
      
      handleCloseDrawer();
      fetchLocations();
    } catch (error: any) {
      console.error('Error saving location:', error);
      if (error.data && error.data.errors) {
        const apiErrors = error.data.errors;
        const newErrors: Record<string, string> = {};
        Object.keys(apiErrors).forEach(key => {
          newErrors[key] = Array.isArray(apiErrors[key]) ? apiErrors[key][0] : apiErrors[key];
        });
        setErrors(newErrors);
      } else {
        toast.error(error.message || 'Failed to save location');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLocation = async (locationId: number) => {
    const location = locations.find(l => l.id === locationId);
    const locationName = location ? location.name : 'this location';
    
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${locationName}. This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await apiService.deleteLocation(locationId);
        
        Swal.fire({
          title: 'Deleted!',
          text: 'Location has been deleted successfully.',
          icon: 'success',
          confirmButtonColor: '#3085d6',
        });
        
        fetchLocations();
      } catch (error: any) {
        console.error('Error deleting location:', error);
        
        Swal.fire({
          title: 'Error!',
          text: error.message || 'Failed to delete location',
          icon: 'error',
          confirmButtonColor: '#3085d6',
        });
      }
    }
  };

  const filteredLocations = locations.filter(location =>
    location.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Settings
        </Typography>
        <Button
          variant="contained"
          startIcon={<LocationOn />}
          onClick={() => handleOpenDrawer()}
          sx={{ 
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: '#4A90E2',
            color: 'white',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#3b82f6',
            },
          }}
        >
          New Locations
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Sidebar - Settings Menu */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SettingsSidebar />
        </Grid>

        {/* Right Content Area */}
        <Grid size={{ xs: 12, md: 9 }}>
          {/* Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    Total Locations
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {locations.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    Active Locations
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {locations.filter(location => location.status === 'active').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    New Locations (This Month)
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {locations.filter(location => {
                      const locationDate = new Date(location.created_at);
                      const currentDate = new Date();
                      return locationDate.getMonth() === currentDate.getMonth() && 
                             locationDate.getFullYear() === currentDate.getFullYear();
                    }).length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              {/* Search and Action Bar */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    placeholder="Search here"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: '#4A90E2' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      width: 400,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#e0f2fe',
                        borderColor: '#e0f2fe',
                        '& fieldset': {
                          borderColor: '#e0f2fe',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bae6fd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4A90E2',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: '#374151',
                        '&::placeholder': {
                          color: '#9ca3af',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    sx={{ 
                      px: 3,
                      py: 1.5,
                      borderRadius: '12px',
                      borderColor: '#e0f2fe',
                      backgroundColor: 'white',
                      color: '#4A90E2',
                      fontWeight: 500,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#bae6fd',
                        backgroundColor: '#f0f9ff',
                      },
                    }}
                  >
                    Filter
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<FileDownload />}
                  sx={{ 
                    px: 3,
                    py: 1.5,
                    borderRadius: '12px',
                    backgroundColor: '#4A90E2',
                    color: 'white',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#3b82f6',
                    },
                  }}
                >
                  Export
                </Button>
              </Box>

              {/* Data Table */}
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb', height: 'calc(100vh - 450px)' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                      <TableCell padding="checkbox">
                        <Checkbox />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Area</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Emirate</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>City</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Landmarks</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Loading locations...
                        </TableCell>
                      </TableRow>
                    ) : filteredLocations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No locations found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLocations
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((location) => (
                        <TableRow key={location.id} hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                          <TableCell padding="checkbox">
                            <Checkbox />
                          </TableCell>
                          <TableCell sx={{ color: '#374151' }}>{location.area || location.name}</TableCell>
                          <TableCell sx={{ color: '#374151' }}>{location.emirate?.name || 'N/A'}</TableCell>
                          <TableCell sx={{ color: '#374151' }}>{location.cityRelation?.name || 'N/A'}</TableCell>
                          <TableCell sx={{ color: '#374151' }}>
                            {location.landmark_1 && location.landmark_2 
                              ? `${location.landmark_1}, ${location.landmark_2}`
                              : location.landmark_1 || location.landmark_2 || 'N/A'
                            }
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={location.status}
                              size="small"
                              sx={{
                                backgroundColor: location.status === 'active' ? '#e0f2f1' : '#fee2e2',
                                color: location.status === 'active' ? '#0d9488' : '#dc2626',
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleOpenDrawer(location)}
                                sx={{
                                  backgroundColor: '#e0f2f1',
                                  color: '#0d9488',
                                  borderRadius: '6px',
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  '&:hover': {
                                    backgroundColor: '#ccedea',
                                  },
                                }}
                              >
                                Update
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleDeleteLocation(location.id)}
                                sx={{
                                  backgroundColor: '#fee2e2',
                                  color: '#dc2626',
                                  borderRadius: '6px',
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  '&:hover': {
                                    backgroundColor: '#fecaca',
                                  },
                                }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredLocations.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Showing"
                labelDisplayedRows={({ from, to, count }) =>
                  `Showing ${from} to ${to} out of ${count} records`
                }
                sx={{
                  '& .MuiTablePagination-selectLabel': {
                    color: '#6b7280',
                  },
                  '& .MuiTablePagination-displayedRows': {
                    color: '#6b7280',
                  },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      color: '#6b7280',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      },
                    },
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Drawer for Add/Edit Location */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 600 },
            maxWidth: '100%',
          },
        }}
      >
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1f2937' }}>
            {isEditing ? 'Edit Location' : 'Add New Location'}
          </Typography>

          <Paper sx={{ p: 3, backgroundColor: '#f9fafb', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
              Location Information
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required error={!!errors.emirate_id}>
                  <InputLabel>Emirate / State</InputLabel>
                  <Select
                    name="emirate_id"
                    value={formData.emirate_id}
                    label="Emirate / State"
                    onChange={handleEmirateChange}
                  >
                    {emirates.map((emirate) => (
                      <MenuItem key={emirate.id} value={emirate.id.toString()}>
                        {emirate.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.emirate_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.emirate_id}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required error={!!errors.city_id}>
                  <InputLabel>City</InputLabel>
                  <Select
                    name="city_id"
                    value={formData.city_id}
                    label="City"
                    onChange={handleSelectChange}
                    disabled={!formData.emirate_id}
                  >
                    {getFilteredCities().map((city) => (
                      <MenuItem key={city.id} value={city.id.toString()}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.city_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.city_id}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  name="area"
                  label="Area"
                  required
                  value={formData.area}
                  onChange={handleInputChange}
                  error={!!errors.area}
                  helperText={errors.area}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="landmark_1"
                  label="Landmark 1"
                  value={formData.landmark_1}
                  onChange={handleInputChange}
                  error={!!errors.landmark_1}
                  helperText={errors.landmark_1}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="landmark_2"
                  label="Landmark 2"
                  value={formData.landmark_2}
                  onChange={handleInputChange}
                  error={!!errors.landmark_2}
                  helperText={errors.landmark_2}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  name="remarks"
                  label="Remarks"
                  multiline
                  rows={3}
                  value={formData.remarks}
                  onChange={handleInputChange}
                  error={!!errors.remarks}
                  helperText={errors.remarks}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  name="address"
                  label="Address"
                  multiline
                  rows={3}
                  value={formData.address}
                  onChange={handleInputChange}
                  error={!!errors.address}
                  helperText={errors.address}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  name="notes"
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Footer Actions */}
          <Box sx={{ display: 'flex', gap: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              variant="outlined"
              onClick={handleCloseDrawer}
              sx={{ flex: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              sx={{ flex: 1 }}
            >
              {submitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update' : 'Create')}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Locations;
